import { motion } from "motion/react";

import MidFidelityPrototype1 from "../../utils/projects/TrainGone/MedFi.png";
import { Badge } from "../ui/badge";
import { ExternalLinkButton } from "../ExternalLink";
import {
  CalloutBox,
  EnhancedListItem,
  KeyInsights,
  ProcessStep,
  ProjectHeader,
  ProjectSection,
  StatsGrid,
  TwoColumnLayout,
} from "./Components";

// Image imports
import Task1 from "../../utils/projects/TrainGone/Task1.png";
import Task2 from "../../utils/projects/TrainGone/Task2.png";
import Task3 from "../../utils/projects/TrainGone/Task3.png";
import TrainGoneCov from "../../utils/projects/TrainGone/TrainGonePage.png";
import { ProjectPageLayout } from "./ProjectPageLayout";

export const TrainGoneProjectPage = () => {
  return (
    <ProjectPageLayout
      projectId="traingone"
      headerMaxWidthClassName="max-w-4xl"
    >
      {/* Overview Image */}
      <ProjectSection id="overview">
        <div className="bg-gradient-to-br from-purple-100/80 to-pink-100/80 rounded-3xl p-8 backdrop-blur-sm">
          <motion.img
            src={TrainGoneCov}
            alt="TrainGone Project Overview"
            className="w-full rounded-2xl shadow-2xl mb-8"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          />
        </div>
      </ProjectSection>

      {/* TL;DR Section */}
      <ProjectSection id="tldr" className="space-y-6">
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-200">
          <h3 className="text-sm font-bold text-purple-600 uppercase tracking-wider mb-3">
            TL;DR
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🔍</span>
                <h4 className="font-bold text-sm">The Problem</h4>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                No way to search for English words from ASL signs—existing
                dictionaries only work if you already know the word
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">💡</span>
                <h4 className="font-bold text-sm">The Solution</h4>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                Reverse ASL dictionary + social platform where users define
                signs through video storytelling
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">📊</span>
                <h4 className="font-bold text-sm">My Impact</h4>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                Led UX research with 6 participants, designed & tested 3
                prototypes, fixed 52 usability issues
              </p>
            </div>
          </div>
        </div>
      </ProjectSection>

      {/* Problem & Solution Section */}
      <ProjectSection id="about" className="space-y-6">
        <ProjectHeader label="Problem & Solution" />

        <div className="grid md:grid-cols-2 gap-6">
          {/* Problem */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-red-50/80 to-orange-50/80 rounded-2xl p-6 border border-red-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                <span className="text-white text-xl">⚠️</span>
              </div>
              <h3 className="font-bold text-lg">The Problem</h3>
            </div>
            <div className="space-y-3 text-sm leading-relaxed text-gray-700">
              <p>
                <strong>Traditional ASL dictionaries are backwards.</strong>{" "}
                They assume you already know the English word and want to find
                the sign.
              </p>
              <p>
                But what if you see a sign and want to know what it means?
                There's <strong>no intuitive way to search</strong> by visual
                parameters like handshape, location, or movement.
              </p>
            </div>
          </motion.div>

          {/* Solution */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-green-50/80 to-emerald-50/80 rounded-2xl p-6 border border-green-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                <span className="text-white text-xl">✅</span>
              </div>
              <h3 className="font-bold text-lg">Our Solution</h3>
            </div>
            <div className="space-y-3 text-sm leading-relaxed text-gray-700">
              <p>
                <strong>TrainGone</strong> is a reverse ASL dictionary where
                users can:
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="font-bold text-green-700">🔍</span>
                  <span>Search for words by describing the sign visually</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-green-700">📹</span>
                  <span>
                    Upload videos showing signs in storytelling context
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-green-700">👥</span>
                  <span>
                    Connect with Deaf community for authentic learning
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <StatsGrid
          items={[
            {
              label: "User Interviews",
              value: "6",
              color: "text-purple-600",
            },
            {
              label: "Usability Fixes",
              value: "52",
              color: "text-purple-600",
            },
            {
              label: "Prototypes Tested",
              value: "3",
              color: "text-purple-600",
            },
          ]}
        />
      </ProjectSection>

      {/* User Research Section */}
      <ProjectSection id="research">
        <ProjectHeader label="User Research" />

        <CalloutBox title="Research Approach">
          <p className="text-purple-800 leading-relaxed mb-4">
            I interviewed{" "}
            <strong>6 participants with diverse relationships to ASL</strong> to
            understand the full ecosystem—from native signers to students to
            interpreters.
          </p>
          <div className="flex flex-wrap gap-3">
            {[
              "Deaf native ASL signer",
              "ASL interpreter (2)",
              "AI motion capture developer",
              "6-year ASL student",
              "3-year ASL student",
            ].map((participant, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="bg-white/70 text-purple-800 text-sm px-3 py-1"
              >
                {participant}
              </Badge>
            ))}
          </div>
        </CalloutBox>

        <div className="space-y-6">
          <h3 className="text-2xl font-bold mb-6">Key Insights</h3>

          <EnhancedListItem
            index={0}
            title="Learning Plateau Without Community"
            description="Advanced ASL students hit a wall without access to the Deaf community for immersion. They feel isolated and can't progress beyond textbook learning."
          />

          <EnhancedListItem
            index={1}
            title="Language Barriers, Not Disability"
            description="The divide between Deaf and hearing communities stems from lack of shared language. Deafness is a cultural and linguistic identity, not a disability to be 'fixed.'"
          />

          <EnhancedListItem
            index={2}
            title="Style Matters in Interpretation"
            description="Even proficient interpreters may not fit with Deaf clients due to differences in signing style, accent, or philosophy (literal vs. meaning-based translation)."
          />
        </div>

        <KeyInsights
          title="Cultural Nuances Discovered"
          insights={[
            "Identity is personal: Some embrace 'disabled' label as empowerment, others reject it entirely",
            "Communication varies: Not all Deaf individuals use ASL—some rely on lip-reading, cochlear implants, or assistive tech",
            "No single 'right' way: Interpretation styles differ between literal translation and conveying underlying meaning",
          ]}
        />
      </ProjectSection>

      {/* Insights Through Prototyping */}
      <ProjectSection id="prototyping-insights">
        <ProjectHeader label="Insights Through Prototyping" />

        <CalloutBox title="Our Guiding Question">
          <p className="text-xl font-semibold text-center">
            "How might we <strong>empower ASL speakers</strong> to communicate
            and connect on a global scale?"
          </p>
        </CalloutBox>

        <p className="text-center text-gray-600 mb-8">
          We ran 3 experience prototypes to test different learning mechanics
          and social engagement patterns.
        </p>

        <div className="space-y-4">
          <ProcessStep
            number="1"
            title="Spontaneous Language Practice"
            description="Invited Spanish students to Coupa Café for unstructured conversation practice. Tested if spontaneous immersion builds confidence and fluency faster than structured lessons."
            color="bg-blue-100"
          />
          <ProcessStep
            number="2"
            title="Audio-less Video Platform"
            description="Had users watch TikTok videos with and without audio to measure engagement. Tested whether visual-only content could be equally compelling for storytelling."
            color="bg-blue-100"
          />
          <ProcessStep
            number="3"
            title="Language Karaoke"
            description="Spanish learners watched and sang along to karaoke songs. Tested if musical context and repetition improve retention and enjoyment."
            color="bg-blue-100"
          />
        </div>

        <CalloutBox title="Key Insight">
          <p className="leading-relaxed">
            Testing validated a <strong>social educational platform</strong>{" "}
            where Deaf signers define signs through{" "}
            <strong>video storytelling</strong>, not just clinical
            demonstrations. This bridges the gap between textbook learning and
            authentic cultural immersion.
          </p>
        </CalloutBox>
      </ProjectSection>

      {/* Competitive Analysis */}
      <ProjectSection id="competitive">
        <ProjectHeader label="Competitive Analysis" />

        <CalloutBox title="Market Landscape">
          <p className="leading-relaxed mb-4">
            I analyzed <strong>4 existing platforms</strong> to identify gaps:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white/50 p-4 rounded-xl">
              <h4 className="font-bold mb-2">
                📖 ASL-English Reverse Dictionary
              </h4>
              <p className="text-sm text-gray-600">
                Has reverse search but lacks context—just clinical definitions
                without real-world usage
              </p>
            </div>
            <div className="bg-white/50 p-4 rounded-xl">
              <h4 className="font-bold mb-2">🎥 TikTok</h4>
              <p className="text-sm text-gray-600">
                Great for video storytelling and community, but no structured
                learning or search
              </p>
            </div>
            <div className="bg-white/50 p-4 rounded-xl">
              <h4 className="font-bold mb-2">📝 Urban Dictionary</h4>
              <p className="text-sm text-gray-600">
                Community-driven definitions with context, but
                text-based—doesn't work for visual language
              </p>
            </div>
            <div className="bg-white/50 p-4 rounded-xl">
              <h4 className="font-bold mb-2">🦎 Chameleon</h4>
              <p className="text-sm text-gray-600">
                Video-based ASL learning, but one-directional content without
                community contribution
              </p>
            </div>
          </div>
          <p className="leading-relaxed mt-4">
            <strong>Our opportunity:</strong> Combine Urban Dictionary's
            community model + TikTok's video-first UX + reverse search
            functionality—creating a platform where the Deaf community defines
            signs authentically.
          </p>
        </CalloutBox>
      </ProjectSection>

      {/* Design Process */}
      <ProjectSection id="design-process">
        <ProjectHeader label="Design Process" />

        <TwoColumnLayout
          left={
            <div>
              <h3 className="text-xl font-bold mb-4">Platform Decision</h3>
              <p className="mb-4 text-sm">
                After extensive sketching, I narrowed down to{" "}
                <strong>2 concepts</strong>:
              </p>
              <div className="space-y-3">
                <div className="bg-white/70 p-4 rounded-xl border-2 border-purple-300">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📱</span>
                    <h4 className="font-bold">Mobile App</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Accessible anywhere, portable learning
                  </p>
                  <div className="mt-2 px-3 py-1 bg-purple-100 rounded-full text-xs font-semibold text-purple-700 inline-block">
                    ✓ Selected
                  </div>
                </div>
                <div className="bg-white/50 p-4 rounded-xl opacity-60">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🥽</span>
                    <h4 className="font-bold">VR Experience</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Immersive practice in realistic environments
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Chose mobile for accessibility—VR requires expensive hardware,
                limiting reach to the communities we want to serve.
              </p>
            </div>
          }
          right={
            <div>
              <h3 className="text-xl font-bold mb-4">Core User Tasks</h3>
              <p className="mb-4 text-sm">
                Focused design around <strong>3 primary flows</strong>:
              </p>
              <div className="space-y-3">
                <div className="bg-white/70 p-4 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🔍</span>
                    <div>
                      <h4 className="font-bold text-sm">Search by Sign</h4>
                      <p className="text-xs text-gray-600">
                        Find words using visual parameters
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/70 p-4 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🎯</span>
                    <div>
                      <h4 className="font-bold text-sm">Discover Related</h4>
                      <p className="text-xs text-gray-600">
                        Explore similar signs and variations
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/70 p-4 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📹</span>
                    <div>
                      <h4 className="font-bold text-sm">Contribute Video</h4>
                      <p className="text-xs text-gray-600">
                        Post your own sign definitions
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        />

        {/* Low-Fi Prototypes */}
        <div className="my-12">
          <h3 className="text-2xl font-bold mb-4">Low-Fidelity Prototypes</h3>
          <p className="text-gray-600 mb-6">
            Early sketches testing the 3 core flows before committing to
            high-fidelity design.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                image: Task1,
                title: "Task 1: Search",
                description: "Filter by handshape, location, and movement",
              },
              {
                image: Task2,
                title: "Task 2: Discover",
                description: "Browse similar signs and variations",
              },
              {
                image: Task3,
                title: "Task 3: Contribute",
                description: "Record and upload video definitions",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-gradient-to-br from-purple-100/80 to-pink-100/80 rounded-2xl p-6 backdrop-blur-sm"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full rounded-xl shadow-lg mb-4 max-h-[300px] object-contain"
                />
                <h4 className="font-bold mb-1">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mid-Fi & Testing */}
        <div className="my-12">
          <h3 className="text-2xl font-bold mb-4">
            Mid-Fidelity & Usability Testing
          </h3>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-purple-100/80 to-pink-100/80 rounded-2xl p-6 backdrop-blur-sm mb-6"
          >
            <img
              src={MidFidelityPrototype1}
              alt="Mid-Fidelity Prototype"
              className="w-full rounded-xl shadow-lg mb-4"
            />
            <p className="text-sm text-center text-gray-600">
              Mid-fidelity prototype in Figma with 3 core tasks + signup flow
            </p>
          </motion.div>

          <CalloutBox title="Usability Testing Results">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="text-center">
                <div className="text-5xl font-bold text-orange-600 mb-2">
                  67
                </div>
                <div className="text-sm text-gray-600">
                  Total usability violations found
                </div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-orange-600 mb-2">
                  12
                </div>
                <div className="text-sm text-gray-600">
                  Critical issues (severity 3-4)
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white/70 p-4 rounded-xl">
                <h4 className="font-bold mb-2 text-sm">
                  🤔 Issue: Confusing Terminology
                </h4>
                <p className="text-sm text-gray-700 italic mb-2">
                  "Terms like 'hand shape,' 'palm orientation,' and 'body
                  location' may not be familiar to users unfamiliar with ASL."
                </p>
                <p className="text-sm">
                  <strong>Fix:</strong> Added visual examples and tooltips for
                  each search parameter
                </p>
              </div>

              <div className="bg-white/70 p-4 rounded-xl">
                <h4 className="font-bold mb-2 text-sm">
                  ❓ Issue: Missing Help Documentation
                </h4>
                <p className="text-sm text-gray-700 italic mb-2">
                  "This seems like a complicated app that requires an in-app
                  tutorial. Where's the help?"
                </p>
                <p className="text-sm">
                  <strong>Fix:</strong> Designed onboarding flow with
                  interactive tutorial
                </p>
              </div>

              <div className="bg-white/70 p-4 rounded-xl">
                <h4 className="font-bold mb-2 text-sm">
                  🔀 Issue: Inconsistent Actions
                </h4>
                <p className="text-sm text-gray-700 italic mb-2">
                  "The 'liked' and 'learned' buttons are confusing. Why are they
                  in different places?"
                </p>
                <p className="text-sm">
                  <strong>Fix:</strong> Unified interaction patterns and added
                  consistent navigation
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-green-50 rounded-xl border-2 border-green-200">
              <p className="text-sm font-semibold text-green-800">
                ✅ Result: Addressed <strong>52 design changes</strong> before
                entering development
              </p>
            </div>
          </CalloutBox>
        </div>
      </ProjectSection>

      {/* Final Product */}
      <ProjectSection id="final">
        <ProjectHeader label="Final Product" />

        <TwoColumnLayout
          left={
            <div>
              <h3 className="text-xl font-bold mb-4">Implementation</h3>
              <p className="leading-relaxed mb-4 text-sm">
                Built with <strong>React Native</strong> for cross-platform
                reach—ensuring accessibility for both Android and iOS users in
                the Deaf community.
              </p>
              <div className="space-y-2">
                <Badge variant="secondary" className="mr-2 mb-2">
                  React Native
                </Badge>
                <Badge variant="secondary" className="mr-2 mb-2">
                  Video Recording API
                </Badge>
                <Badge variant="secondary" className="mr-2 mb-2">
                  iOS + Android
                </Badge>
              </div>
            </div>
          }
          right={
            <div>
              <h3 className="text-xl font-bold mb-4">Key Features</h3>
              <ul className="space-y-3">
                <li className="bg-white/70 p-4 rounded-xl">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">📹</span>
                    <div>
                      <h4 className="font-bold text-sm">Video Creation</h4>
                      <p className="text-xs text-gray-600">
                        Fully functional recording & upload system
                      </p>
                    </div>
                  </div>
                </li>
                <li className="bg-white/70 p-4 rounded-xl">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">🔍</span>
                    <div>
                      <h4 className="font-bold text-sm">Visual Search</h4>
                      <p className="text-xs text-gray-600">
                        Filter by handshape, location, movement (mock data)
                      </p>
                    </div>
                  </div>
                </li>
                <li className="bg-white/70 p-4 rounded-xl">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">👥</span>
                    <div>
                      <h4 className="font-bold text-sm">Community Platform</h4>
                      <p className="text-xs text-gray-600">
                        Social features for storytelling & connection
                      </p>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          }
        />

        <CalloutBox title="Development Trade-offs">
          <p className="leading-relaxed">
            The{" "}
            <strong>
              most complex feature—video creation—was fully implemented
            </strong>{" "}
            to validate technical feasibility. Search and browse relied on
            hard-coded data since we didn't have an existing user base yet, but
            this approach provided a{" "}
            <strong>realistic demonstration of the full user experience</strong>{" "}
            for testing and stakeholder demos.
          </p>
        </CalloutBox>
      </ProjectSection>

      {/* Reflection */}
      <ProjectSection id="reflection">
        <ProjectHeader label="Reflection & Learnings" />

        <CalloutBox title="What I Learned">
          <div className="space-y-4">
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2">
                🎯 Research Drives Design Decisions
              </h4>
              <p className="text-sm leading-relaxed">
                Interviewing 6 diverse participants revealed nuances I never
                would have discovered through assumptions—like how Deaf identity
                is cultural, not medical. This fundamentally shaped our
                platform's tone and approach.
              </p>
            </div>
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2">
                🔄 Iterate Based on Real Feedback
              </h4>
              <p className="text-sm leading-relaxed">
                Finding 67 usability violations was humbling but invaluable.
                Addressing 52 issues before development saved weeks of rework
                and resulted in a much stronger product.
              </p>
            </div>
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2">
                🤝 Design for Communities, Not Users
              </h4>
              <p className="text-sm leading-relaxed">
                This wasn't about building a tool—it was about bridging two
                communities. The social platform approach emerged from
                understanding that language learning is inherently social and
                cultural.
              </p>
            </div>
          </div>
        </CalloutBox>
      </ProjectSection>

      <ProjectSection id="demo-link">
        <ExternalLinkButton
          href="https://www.youtube.com/watch?v=--dKGtDRh2A"
          label="Watch Demo Video"
        />
      </ProjectSection>
    </ProjectPageLayout>
  );
};
